/**
 * RelationshipChooserModal Component
 *
 * Modal for choosing which relationship type to add.
 * Entry point for future relationship creation flows.
 *
 * Currently supports selection only - actual creation flows
 * will be implemented in the next phase.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Configuration flag to control visibility of Brother/Sister options
// Set to true to show Brother/Sister options, false to hide them
const ENABLE_SIBLING_RELATIONSHIPS = true;

export type RelationshipType = 'father' | 'mother' | 'brother' | 'sister' | 'son' | 'daughter' | 'spouse';

interface RelationshipOption {
  type: RelationshipType;
  label: string;
  icon: string;
  color: string;
  description: string;
}

interface RelationshipChooserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectRelationship: (type: RelationshipType) => void;
  personName?: string;
}

export function RelationshipChooserModal({
  isOpen,
  onClose,
  onSelectRelationship,
  personName,
}: RelationshipChooserModalProps) {
  const { t } = useTranslation('common');
  const [selectedType, setSelectedType] = useState<RelationshipType | null>(null);

  // Relationship options with translations
  const RELATIONSHIP_OPTIONS: RelationshipOption[] = [
    {
      type: 'father',
      label: t('tree.relationshipChooser.relationships.father.label'),
      icon: 'man',
      color: 'blue',
      description: t('tree.relationshipChooser.relationships.father.description'),
    },
    {
      type: 'mother',
      label: t('tree.relationshipChooser.relationships.mother.label'),
      icon: 'woman',
      color: 'pink',
      description: t('tree.relationshipChooser.relationships.mother.description'),
    },
    // Conditionally include Brother option based on the configuration flag
    ...(ENABLE_SIBLING_RELATIONSHIPS
      ? [
          {
            type: 'brother' as RelationshipType,
            label: t('tree.relationshipChooser.relationships.brother.label'),
            icon: 'man_2',
            color: 'indigo',
            description: t('tree.relationshipChooser.relationships.brother.description'),
          },
        ]
      : []),
    // Conditionally include Sister option based on the configuration flag
    ...(ENABLE_SIBLING_RELATIONSHIPS
      ? [
          {
            type: 'sister' as RelationshipType,
            label: t('tree.relationshipChooser.relationships.sister.label'),
            icon: 'woman_2',
            color: 'purple',
            description: t('tree.relationshipChooser.relationships.sister.description'),
          },
        ]
      : []),
    {
      type: 'son',
      label: t('tree.relationshipChooser.relationships.son.label'),
      icon: 'child_care',
      color: 'emerald',
      description: t('tree.relationshipChooser.relationships.son.description'),
    },
    {
      type: 'daughter',
      label: t('tree.relationshipChooser.relationships.daughter.label'),
      icon: 'face_3',
      color: 'rose',
      description: t('tree.relationshipChooser.relationships.daughter.description'),
    },
    {
      type: 'spouse',
      label: t('tree.relationshipChooser.relationships.spouse.label'),
      icon: 'favorite',
      color: 'red',
      description: t('tree.relationshipChooser.relationships.spouse.description'),
    },
  ];

  if (!isOpen) return null;

  const handleSelect = (type: RelationshipType) => {
    setSelectedType(type);
    // Small delay for visual feedback
    setTimeout(() => {
      onSelectRelationship(type);
      setSelectedType(null);
    }, 150);
  };

  const getColorClasses = (color: string, isSelected: boolean) => {
    const baseClasses = {
      blue: isSelected
        ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600'
        : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 border-slate-200 dark:border-slate-700',
      pink: isSelected
        ? 'bg-pink-100 dark:bg-pink-900/40 border-pink-400 dark:border-pink-600'
        : 'hover:bg-pink-50 dark:hover:bg-pink-900/20 border-slate-200 dark:border-slate-700',
      indigo: isSelected
        ? 'bg-indigo-100 dark:bg-indigo-900/40 border-indigo-400 dark:border-indigo-600'
        : 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-slate-200 dark:border-slate-700',
      purple: isSelected
        ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-400 dark:border-purple-600'
        : 'hover:bg-purple-50 dark:hover:bg-purple-900/20 border-slate-200 dark:border-slate-700',
      emerald: isSelected
        ? 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-400 dark:border-emerald-600'
        : 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-slate-200 dark:border-slate-700',
      rose: isSelected
        ? 'bg-rose-100 dark:bg-rose-900/40 border-rose-400 dark:border-rose-600'
        : 'hover:bg-rose-50 dark:hover:bg-rose-900/20 border-slate-200 dark:border-slate-700',
      red: isSelected
        ? 'bg-red-100 dark:bg-red-900/40 border-red-400 dark:border-red-600'
        : 'hover:bg-red-50 dark:hover:bg-red-900/20 border-slate-200 dark:border-slate-700',
    };
    return baseClasses[color as keyof typeof baseClasses];
  };

  const getIconColorClasses = (color: string) => {
    const iconColors = {
      blue: 'text-blue-600 dark:text-blue-400',
      pink: 'text-pink-600 dark:text-pink-400',
      indigo: 'text-indigo-600 dark:text-indigo-400',
      purple: 'text-purple-600 dark:text-purple-400',
      emerald: 'text-emerald-600 dark:text-emerald-400',
      rose: 'text-rose-600 dark:text-rose-400',
      red: 'text-red-600 dark:text-red-400',
    };
    return iconColors[color as keyof typeof iconColors];
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 md:p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-md md:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 p-3 sm:p-4 md:p-6 border-b border-primary/20 sticky top-0 z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4 flex-1">
              <div className="size-9 sm:size-10 md:size-12 rounded-full bg-primary/20 dark:bg-primary/30 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-primary text-lg sm:text-xl md:text-2xl">
                  family_restroom
                </span>
              </div>
              <div className="flex-1 pt-0.5 sm:pt-0.5 md:pt-1">
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-slate-900 dark:text-white mb-0.5 sm:mb-1 leading-tight">
                  {t('tree.relationshipChooser.title')}
                </h3>
                <p className="text-xs sm:text-sm md:text-sm text-slate-600 dark:text-slate-400 leading-snug">
                  {personName ? t('tree.relationshipChooser.subtitleWithName', { name: personName }) : t('tree.relationshipChooser.subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-2 sm:ml-3 md:ml-4 p-1 sm:p-1.5 rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-lg sm:text-xl">
                close
              </span>
            </button>
          </div>
        </div>

        {/* Options Grid */}
        <div className="p-3 sm:p-4 md:p-6">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {RELATIONSHIP_OPTIONS.map((option) => {
              const isSelected = selectedType === option.type;
              return (
                <button
                  key={option.type}
                  onClick={() => handleSelect(option.type)}
                  className={`
                    p-3 sm:p-4 border-2 rounded-lg sm:rounded-xl transition-all duration-200
                    ${getColorClasses(option.color, isSelected)}
                  `}
                >
                  <div className="flex flex-col items-center gap-1.5 sm:gap-2 text-center">
                    <div className={`size-8 sm:size-9 md:size-10 rounded-full bg-white/80 dark:bg-slate-800/80 flex items-center justify-center ${getIconColorClasses(option.color)}`}>
                      <span className="material-symbols-outlined text-lg sm:text-xl md:text-2xl">
                        {option.icon}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-xs sm:text-sm text-slate-900 dark:text-white">
                        {option.label}
                      </div>
                      <div className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-tight">
                        {option.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
